import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import type { Session, SupabaseClient } from "@supabase/supabase-js";
import { cloudSyncEnabled, config } from "../config";

export type AuthState =
  | { status: "guest" }
  | { status: "loading" }
  | { status: "signedIn"; userId: string; email: string | null };

interface AuthContextValue {
  state: AuthState;
  /** null sin Supabase configurado: la web funciona en modo invitado. */
  client: SupabaseClient | null;
  signInWithEmail(email: string): Promise<void>;
  signOut(): Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

function toState(session: Session | null): AuthState {
  return session ? { status: "signedIn", userId: session.user.id, email: session.user.email ?? null } : { status: "guest" };
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [client, setClient] = useState<SupabaseClient | null>(null);
  const [state, setState] = useState<AuthState>(cloudSyncEnabled ? { status: "loading" } : { status: "guest" });

  useEffect(() => {
    if (!cloudSyncEnabled) return;
    let unsubscribe = () => {};
    let cancelled = false;
    // supabase-js se carga bajo demanda: quien no usa cuentas no paga su peso.
    void import("@supabase/supabase-js").then(({ createClient }) => {
      if (cancelled) return;
      const supabase = createClient(config.supabaseUrl, config.supabaseAnonKey, {
        auth: { persistSession: true, autoRefreshToken: true, detectSessionInUrl: true },
      });
      setClient(supabase);
      void supabase.auth.getSession().then(({ data }) => setState(toState(data.session)));
      const { data } = supabase.auth.onAuthStateChange((_event, session) => setState(toState(session)));
      unsubscribe = () => data.subscription.unsubscribe();
    });
    return () => {
      cancelled = true;
      unsubscribe();
    };
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      state,
      client,
      async signInWithEmail(email) {
        if (!client) throw new Error("Las cuentas no están configuradas");
        const { error } = await client.auth.signInWithOtp({ email, options: { emailRedirectTo: window.location.origin } });
        if (error) throw error;
      },
      async signOut() {
        await client?.auth.signOut();
      },
    }),
    [state, client]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth fuera de AuthProvider");
  return ctx;
}
